import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, Search, Users, Pencil, Trash2, X, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["Superadmin", "Admin", "Lead Manager", "Designer", "Printing Technician", "Runner", "Delivery Staff"];

const roleColors = {
  Superadmin: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  Admin: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  "Lead Manager": { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  Designer: { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-200" },
  "Printing Technician": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  Runner: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  "Delivery Staff": { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const { data } = await axios.get(`${API}/staff`, { headers: getAuthHeaders(), withCredentials: true });
      setStaff(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAdd = () => {
    setEditingStaff(null);
    setForm({ name: "", email: "", password: "", phone: "", role: "" });
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingStaff(s);
    setForm({ name: s.name, email: s.email, password: "", phone: s.phone || "", role: s.role });
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      if (editingStaff) {
        const updateData = { name: form.name, phone: form.phone, role: form.role };
        await axios.put(`${API}/staff/${editingStaff.id}`, updateData, { headers: getAuthHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API}/staff`, form, { headers: getAuthHeaders(), withCredentials: true });
      }
      setShowForm(false);
      fetchStaff();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      await axios.delete(`${API}/staff/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      fetchStaff();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Failed to delete.");
    }
  };

  const filtered = staff.filter((s) => {
    const matchSearch = !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === "All" || s.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleCounts = { All: staff.length };
  ROLES.forEach(r => { roleCounts[r] = staff.filter(s => s.role === r).length; });

  return (
    <div data-testid="page-staff" className="min-h-screen bg-[#F4F3EF]">
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Staff</h1>
        <button onClick={openAdd} data-testid="add-staff-btn"
          className="inline-flex items-center gap-2 bg-[#161616] text-white font-bold px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200">
          <Plus size={16} /> Add Staff
        </button>
      </div>

      <div className="px-5 sm:px-8 py-6 space-y-6">
        {/* Role filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {["All", ...ROLES].map((r) => (
            <button key={r} onClick={() => setFilterRole(r)} data-testid={`staff-filter-${r.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex-shrink-0 px-4 py-2 text-xs font-bold border transition-colors ${filterRole === r ? "bg-[#161616] text-white border-[#161616]" : "bg-white text-[#5A5955] border-[#D9D7D0] hover:border-[#161616]"}`}>
              {r} ({roleCounts[r] || 0})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5955]" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..."
            data-testid="staff-search" className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D9D7D0] text-sm focus:border-[#161616] outline-none transition-colors" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-[#5A5955]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#D9D7D0] p-12 text-center">
            <Users size={36} className="mx-auto text-[#D9D7D0] mb-4" />
            <p className="text-sm text-[#5A5955]">No staff found.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#D9D7D0] overflow-x-auto">
            <table className="w-full text-left" data-testid="staff-table">
              <thead>
                <tr className="border-b border-[#D9D7D0]">
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Name</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Email</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Phone</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Role</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const rc = roleColors[s.role] || roleColors.Admin;
                  return (
                    <tr key={s.id} className="border-b border-[#D9D7D0] hover:bg-[#F7F6F2] transition-colors" data-testid={`staff-row-${s.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#161616] text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                            {s.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <span className="text-sm font-medium text-[#161616]">{s.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{s.email}</td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{s.phone || "—"}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-bold ${rc.bg} ${rc.text} border ${rc.border}`}>
                          <Shield size={10} />{s.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`} className="p-2 hover:bg-[#F4F3EF] transition-colors" title="Edit">
                            <Pencil size={14} className="text-[#5A5955]" />
                          </button>
                          <button onClick={() => handleDelete(s.id)} data-testid={`delete-staff-${s.id}`} className="p-2 hover:bg-red-50 transition-colors" title="Delete">
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Staff Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="staff-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-xl font-bold tracking-tight text-[#161616]">{editingStaff ? "Edit Staff" : "Add New Staff"}</h2>
              <button onClick={() => setShowForm(false)} data-testid="close-staff-modal" className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <label className="field-label">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="staff-input-name"
                  className="field" placeholder="Full name" />
              </div>
              <div>
                <label className="field-label">Email *</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} disabled={!!editingStaff} data-testid="staff-input-email"
                  className={`field ${editingStaff ? "opacity-50 cursor-not-allowed" : ""}`} placeholder="email@example.com" />
              </div>
              {!editingStaff && (
                <div>
                  <label className="field-label">Password *</label>
                  <input type="password" required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="staff-input-password"
                    className="field" placeholder="Set a password" />
                </div>
              )}
              <div>
                <label className="field-label">Phone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="staff-input-phone"
                  className="field" placeholder="+91 XXXXX XXXXX" />
              </div>
              <div>
                <label className="field-label">Role *</label>
                <select required value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="staff-input-role"
                  className="field">
                  <option value="">Select a role</option>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              {formError && <p data-testid="staff-form-error" className="text-sm text-red-600 bg-red-50 px-4 py-2 border border-red-200">{formError}</p>}

              <button type="submit" disabled={submitting} data-testid="submit-staff-btn"
                className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200 disabled:opacity-50">
                {submitting ? "Saving..." : editingStaff ? "Update Staff" : "Add Staff"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
